import { ArrowLeftIcon, ArrowDownIcon, ArrowUpIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { PropertyConfigFields } from '../../components/studio/PropertyConfigFields';
import { PropertyNavigator } from '../../components/studio/PropertyNavigator';
import { PropertyTypeSelect } from '../../components/studio/PropertyTypeSelect';
import { useToast } from '../../components/ui/toast';
import { describeApiError } from '../../data/http';
import { getTemplate, listPropertyTypes, listSystemProperties, getVariableCatalogue, saveTemplateProperties } from '../../data/templates';
import { listReferenceEntries } from '../../data/reference';
import { listServiceOptions } from '../../data/services';
import { referenceUseFor, serviceResponseTypeFor } from '../../components/studio/propertyConfigUi';
import { validateDefaultValue } from '../../data/defaultValueRules';
import { TokenField } from '../../components/studio/TokenField';
import { buildSections, type ReferenceOption } from '../../components/studio/variableSections';
import { VisibilityEditor } from '../../components/studio/VisibilityEditor';
import type {
  PropertyTypeDefinition,
  ServiceOption,
  SystemPropertySummary,
  TemplatePropertySummary,
  VariableCatalogue
} from '../../data/types';

const EMPTY_CATALOGUE: VariableCatalogue = { variables: [], transforms: [], expandable: {} };

/** Every use a reference entry can be bound to; the Default Value picker offers the one matching the property's own type. */
const REFERENCE_USES = ['text', 'image', 'table', 'document'];

function parseConfig(configJson: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(configJson || '{}');
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function PropertyEditorPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { templateId } = useParams();

  const [templateName, setTemplateName] = useState('');
  const [properties, setProperties] = useState<TemplatePropertySummary[]>([]);
  const [types, setTypes] = useState<PropertyTypeDefinition[]>([]);
  const [allTypes, setAllTypes] = useState<PropertyTypeDefinition[]>([]);
  const [catalogue, setCatalogue] = useState<VariableCatalogue>(EMPTY_CATALOGUE);
  const [references, setReferences] = useState<Record<string, ReferenceOption[]>>({});
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [systemProperties, setSystemProperties] = useState<SystemPropertySummary[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rowRefs = useRef(new Map<string, HTMLLIElement>());

  const load = useCallback(() => {
    if (!templateId) return Promise.resolve();
    return Promise.all([
      getTemplate(templateId),
      listPropertyTypes(),
      listSystemProperties(),
      getVariableCatalogue(templateId),
      Promise.all(REFERENCE_USES.map((use) => listReferenceEntries({ use }))),
      listServiceOptions()
    ])
      .then(([detail, definitions, system, vocabulary, referenceLists, services]) => {
        setTemplateName(detail.template.name);
        setProperties(detail.properties);
        setAllTypes(definitions);
        setTypes(
          definitions.filter(
            (definition) =>
              definition.id !== 'List' &&
              (!definition.formats || definition.formats.includes(detail.template.documentType))
          )
        );
        setSystemProperties(system);
        setCatalogue(vocabulary);
        setReferences(
          Object.fromEntries(
            REFERENCE_USES.map((use, i) => [
              use,
              (referenceLists[i]?.items ?? []).map((entry) => ({
                key: entry.key,
                scope: entry.scope,
                title: entry.title,
                description: entry.description,
                kind: entry.kind
              }))
            ])
          )
        );
        setServiceOptions(services);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t('errors.loadTemplates'));
      })
      .finally(() => setLoading(false));
  }, [templateId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = (index: number, changes: Partial<TemplatePropertySummary>) => {
    setProperties((current) => current.map((p, i) => (i === index ? { ...p, ...changes } : p)));
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= properties.length) return;
    setProperties((current) => {
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const jumpTo = (tokenName: string) => {
    const row = rowRefs.current.get(tokenName);
    row?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    row?.querySelector('input')?.focus();
  };

  const save = async () => {
    if (!templateId || busy) return;
    setBusy(true);
    try {
      const saved = await saveTemplateProperties(
        templateId,
        properties.map((p) => ({
          tokenName: p.tokenName,
          displayName: p.displayName,
          propertyType: p.propertyType,
          isRequired: p.isRequired,
          isAdvanced: p.isAdvanced,
          description: p.description,
          defaultValue: p.defaultValue,
          visibilityConditionJson: p.visibilityConditionJson,
          configJson: p.configJson
        }))
      );
      setProperties(saved);
      toast({ title: t('properties.saved'), type: 'success' });
    } catch (err) {
      toast({ title: describeApiError(err, t('errors.saveProperties')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const field =
    'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';
  const iconButton =
    'inline-flex items-center justify-center rounded border border-input bg-surface p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <>
      <Link
        to={`/studio/templates/${templateId}`}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <ArrowLeftIcon className="size-4" aria-hidden="true" />
        {templateName || t('studio.templateDetail.title')}
      </Link>

      <h1 className="mt-3 text-3xl font-heading font-semibold tracking-normal text-foreground">
        {t('properties.title')}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('properties.subtitle')}</p>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {!loading && !error ? (
        <div className="mt-6 lg:grid lg:grid-cols-[1fr_260px] lg:items-start lg:gap-6">
          <div>
            <ol className="grid gap-3">
              {properties.map((property, index) => {
              // `allTypes` is the full registry; `types` is what the picker offers (List is
              // deliberately excluded there — it's system-property-only, never hand-assigned).
              // A property already bound to List is a real, valid type, just not one shown
              // as an option — resolve it here so it doesn't read as "not registered".
              const definition = allTypes.find((d) => d.id === property.propertyType);
              const unregistered = !definition;
              const systemManagedType = definition && !types.some((d) => d.id === definition.id) ? definition : undefined;
              const inherited = systemProperties.find((s) => s.tokenName === property.tokenName);
              const inheritedAdvanced = inherited?.isAdvanced ?? false;
              // Nothing to prefill means the wizard must open the disclosure every time.
              const alwaysExpands =
                property.isRequired &&
                (property.isAdvanced ?? inheritedAdvanced) &&
                !property.defaultValue &&
                !inherited?.defaultValue;

              // Only earlier properties: a reference forward would always be empty.
              const ordered = properties.map((p) => ({ tokenName: p.tokenName, propertyType: p.propertyType }));
              const sections = buildSections({
                catalogue,
                fields: properties.slice(0, index).map((p) => ({
                  tokenName: p.tokenName,
                  displayName: p.displayName || p.tokenName,
                  propertyType: p.propertyType
                })),
                reference: references[referenceUseFor(property.propertyType)] ?? [],
                currentFieldName: property.displayName || property.tokenName,
                t
              });
              const defaultValueErrors = validateDefaultValue(ordered, index, property.defaultValue, catalogue);

              // Only earlier properties of the wanted type: mirrors the default-value
              // variable picker's "no forward references" rule above.
              const linkedPropertyOptions = (propertyType: string): ReferenceOption[] =>
                properties
                  .slice(0, index)
                  .filter((p) => p.propertyType === propertyType)
                  .map((p) => ({ key: p.tokenName, scope: '', title: p.displayName || p.tokenName, description: '' }));

              return (
                <li
                  key={property.tokenName}
                  ref={(el) => {
                    if (el) rowRefs.current.set(property.tokenName, el);
                    else rowRefs.current.delete(property.tokenName);
                  }}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (dragIndex !== null) move(dragIndex, index);
                    setDragIndex(null);
                  }}
                  className="rounded-lg border border-border bg-surface p-4"
                >
                  <div className="flex items-start gap-3">
                    <Bars3Icon className="mt-2 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />

                    <div className="min-w-0 flex-1">
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="text-sm">
                          <span className="font-medium text-foreground">{t('properties.token')}</span>
                          <p
                            className="mt-1 truncate rounded-sm border border-border bg-muted px-3 py-2 font-mono text-sm text-muted-foreground"
                            title={property.tokenName}
                          >
                            {property.tokenName}
                          </p>
                        </div>

                        <label className="block text-sm">
                          <span className="font-medium text-foreground">{t('properties.displayName')}</span>
                          <input
                            type="text"
                            value={property.displayName}
                            onChange={(event) => update(index, { displayName: event.target.value })}
                            className={field}
                          />
                        </label>

                        <label className="block text-sm">
                          <span className="font-medium text-foreground">{t('properties.type')}</span>
                          <PropertyTypeSelect
                            types={types}
                            value={property.propertyType}
                            onChange={(next) => update(index, { propertyType: next, configJson: '{}' })}
                            currentDefinition={systemManagedType}
                            unregisteredLabel={
                              unregistered ? t('properties.unregistered', { type: property.propertyType }) : undefined
                            }
                          />
                        </label>
                      </div>

                      <label className="mt-4 block border-t border-border pt-4 text-sm">
                        <span className="font-medium text-foreground">{t('properties.description')}</span>
                        <textarea
                          rows={2}
                          value={property.description}
                          onChange={(event) => update(index, { description: event.target.value })}
                          className={field}
                        />
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {t('properties.descriptionHint')}
                        </span>
                      </label>

                      {systemManagedType ? (
                        <p className="mt-4 border-t border-border pt-4 text-sm text-muted-foreground">
                          {t('properties.systemManagedTypeHint', { type: t(`propertyTypes.${systemManagedType.id}`) })}
                        </p>
                      ) : definition ? (
                        <div className="mt-4 border-t border-border pt-4">
                          <PropertyConfigFields
                            schema={definition.configSchema}
                            config={parseConfig(property.configJson)}
                            onChange={(next) => update(index, { configJson: JSON.stringify(next) })}
                            defaultValue={property.defaultValue}
                            tableReferenceOptions={references.table ?? []}
                            imageReferenceOptions={references.image ?? []}
                            linkedPropertyOptions={linkedPropertyOptions}
                            services={serviceOptions.filter((s) => s.responseType === serviceResponseTypeFor(property.propertyType))}
                            variableSections={sections}
                            valueSources={definition.valueSources}
                            onDefaultValueChange={(next) => update(index, { defaultValue: next })}
                          />
                        </div>
                      ) : (
                        <p className="mt-4 border-t border-border pt-4 text-sm text-destructive">
                          {t('properties.unregisteredHint', { type: property.propertyType })}
                        </p>
                      )}

                      <div className="mt-4 border-t border-border pt-4">
                        <TokenField
                          label={t('properties.defaultValue')}
                          value={property.defaultValue}
                          onChange={(next) => update(index, { defaultValue: next })}
                          sections={sections}
                          transforms={catalogue.transforms}
                          placeholder={inherited ? inherited.defaultValue : t('properties.defaultValuePlaceholder')}
                          errors={defaultValueErrors}
                        >
                          <span className="mt-1 block text-xs text-muted-foreground">
                            {inherited
                              ? t('properties.defaultValueInherited', { value: inherited.defaultValue })
                              : t('properties.defaultValueHint', { token: '{{@Key}}' })}
                          </span>
                        </TokenField>
                      </div>

                      <div className="mt-4 border-t border-border pt-4">
                        <VisibilityEditor
                          value={property.visibilityConditionJson}
                          tokens={properties
                            .filter((other) => other.tokenName !== property.tokenName)
                            .map((other) => other.tokenName)}
                          onChange={(json) => update(index, { visibilityConditionJson: json })}
                        />
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-border pt-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={property.isRequired}
                            onChange={(event) => update(index, { isRequired: event.target.checked })}
                            className="size-4 rounded border-input"
                          />
                          <span className="text-foreground">{t('properties.required')}</span>
                        </label>

                        <span className="flex items-center gap-2 text-sm">
                          <label htmlFor={`advanced-${property.tokenName}`} className="text-foreground">
                            {t('properties.advancedLabel')}
                          </label>
                          <select
                            id={`advanced-${property.tokenName}`}
                            value={
                              property.isAdvanced == null
                                ? inherited
                                  ? 'inherit'
                                  : 'off'
                                : property.isAdvanced
                                  ? 'on'
                                  : 'off'
                            }
                            onChange={(event) =>
                              update(index, {
                                isAdvanced: event.target.value === 'inherit' ? null : event.target.value === 'on'
                              })
                            }
                            className="rounded-sm border border-input bg-surface px-2 py-1 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
                          >
                            {inherited ? (
                              <option value="inherit">
                                {t('properties.advancedInherited', {
                                  value: t(inheritedAdvanced ? 'properties.advancedOn' : 'properties.advancedOff')
                                })}
                              </option>
                            ) : null}
                            <option value="on">{t('properties.advancedOn')}</option>
                            <option value="off">{t('properties.advancedOff')}</option>
                          </select>
                        </span>
                      </div>

                      <p className="mt-2 text-xs text-muted-foreground">{t('properties.advancedHint')}</p>

                      {alwaysExpands ? (
                        <p className="mt-1 text-xs text-destructive">{t('properties.advancedRequiredHint')}</p>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => move(index, index - 1)}
                        disabled={index === 0}
                        aria-label={t('properties.moveUp', { name: property.tokenName })}
                        className={iconButton}
                      >
                        <ArrowUpIcon className="size-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(index, index + 1)}
                        disabled={index === properties.length - 1}
                        aria-label={t('properties.moveDown', { name: property.tokenName })}
                        className={iconButton}
                      >
                        <ArrowDownIcon className="size-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </li>
              );
              })}
            </ol>

            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || properties.length === 0}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? t('properties.saving') : t('properties.save')}
            </button>
          </div>

          <aside className="hidden lg:sticky lg:top-24 lg:block">
            <PropertyNavigator
              properties={properties}
              types={allTypes}
              systemProperties={systemProperties}
              move={move}
              onJump={jumpTo}
            />
          </aside>
        </div>
      ) : null}
    </>
  );
}
